import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus } from "lucide-react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import EmptyState from "../../components/shared/EmptyState";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import Input from "../../components/Inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import { FormLabel } from "../../components/Labels/labels";

import Loader from "../../components/Loader/Loader";
import UserDetailPanel from "../../components/Settings/UserDetailPanel";
import { listAdminUsers, inviteAdminUser, disableAdminUser, restoreAdminUser, updateAdminUserOrgMode, updateAdminUserName, resetAdminUserPassword, resendAdminUserInvite, cancelAdminUserInvite, purgeAdminUser, getOrgSettings, updateOrgSettings } from "../../api/adminUsers";
import { AuthError } from "../../api";
import { getUserGroups } from "../../api/adminGroups";
import UserAuthStore from "../../store/userStore";
import useFeedback from "../../hooks/useFeedback";
import Toggle from "../../components/shared/Toggle";
import logger from "../../services/Logger";
import { getUserInviteFeedback } from "./userInviteFeedback";

// ─── Avatar initials ──────────────────────────────────────────────────────────
const UserAvatar = ({ name, email }) => {
  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : (email || "?")[0].toUpperCase();

  return (
    <div
      className="photon-avatar w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
    >
      <span className="text-sm font-semibold mt-[1px]">{initials}</span>
    </div>
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const getUserStatus = (user) => {
  if (user.disabled) return "disabled";
  if (!user.invite_token_used) {
    const expires = user.invite_token_expires_at ? new Date(user.invite_token_expires_at) : null;
    if (expires && expires > new Date()) return "invited";
    if (expires) return "expired";
  }
  return "active";
};

const STATUS_STYLES = {
  active:   { badge: "bg-fill-green-100 text-ink-green-800 dark:bg-fill-green-600/20 dark:text-ink-green-300", dot: "bg-fill-green-600",  label: "Active"  },
  invited:  { badge: "bg-fill-hex-fef9c3 text-ink-yellow-800 dark:bg-fill-yellow-600/20 dark:text-ink-hex-fde047", dot: "bg-fill-yellow-600",  label: "Invited" },
  expired:  { badge: "bg-fill-hex-ffedd5 text-ink-orange-700 dark:bg-fill-orange-600/20 dark:text-ink-orange-300", dot: "bg-fill-orange-600",  label: "Expired" },
  disabled: { badge: "bg-fill-red-100 text-ink-red-800 dark:bg-fill-red-600/20 dark:text-ink-red-300", dot: "bg-fill-red-600",  label: "Disabled" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.active;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${s.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ─── Admin badge ──────────────────────────────────────────────────────────────
const AdminBadge = () => (
  <span className="photon-metadata-label inline-flex items-center text-[10px] font-medium text-ink-violet-600 dark:text-ink-hex-c4b5fd bg-fill-hex-ede9fe dark:bg-fill-violet-600/20 px-2 py-0.5 rounded-full">
    Admin
  </span>
);

// ─── External badge ───────────────────────────────────────────────────────────
const ExternalBadge = () => (
  <span className="photon-metadata-label inline-flex items-center text-[10px] font-medium text-ink-hex-d97706 dark:text-ink-amber-300 bg-fill-hex-fef3c7 dark:bg-fill-hex-d97706/20 px-2 py-0.5 rounded-full">
    External
  </span>
);

// ─── Inline Invite Form ───────────────────────────────────────────────────────
const InviteForm = ({ orgDomain, orgSettingsStatus, onSuccess, onCancel }) => {
  const { showFeedback } = useFeedback();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [allowExternal, setAllowExternal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const emailDomain = email.includes("@") ? email.trim().split("@")[1]?.toLowerCase() : null;
  const normalizedOrgDomain = orgDomain.trim().toLowerCase();
  const looksExternal = emailDomain && normalizedOrgDomain && emailDomain !== normalizedOrgDomain;
  const settingsError = orgSettingsStatus === "loading"
    ? "Loading organization settings..."
    : orgSettingsStatus !== "ready"
      ? "Organization settings could not be loaded. Reload the page before inviting users."
      : !normalizedOrgDomain
        ? "Organization domain is not configured. Configure it before inviting users."
        : "";
  const inviteBlocked = Boolean(settingsError || (looksExternal && !allowExternal));

  const handleSubmit = async () => {
    if (loading || inviteBlocked) return;
    setError("");
    setSubmitted(true);
    if (!email.trim() || !name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return;
    setLoading(true);
    try {
      const result = await inviteAdminUser(email.trim(), name.trim(), isAdmin, allowExternal);
      const feedback = getUserInviteFeedback(result);
      showFeedback(feedback.message, feedback.type);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to invite user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      key="invite-form"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl mb-3 p-5">
        <p className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide mb-4">
          New Invite
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 flex flex-col gap-1.5">
            <FormLabel>
              Email <span className="text-ink-red-600">*</span>
            </FormLabel>
            <Input
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
              className={submitted && (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) ? "border-edge-red-600 dark:border-edge-red-600" : ""}
            />
          </div>
          <div className="flex-1 flex flex-col gap-1.5">
            <FormLabel>Full Name <span className="text-ink-red-600">*</span></FormLabel>
            <Input
              type="text"
              placeholder="Jane Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              className={submitted && !name.trim() ? "border-edge-red-600 dark:border-edge-red-600" : ""}
            />
          </div>
        </div>

        {/* Admin toggle */}
        <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-fill-hex-f5f3ff dark:bg-fill-violet-600/10 border border-edge-hex-ddd6fe dark:border-edge-violet-600/30 mb-3">
          <div>
            <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Admin access</p>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
              Grants access to user management and admin settings
            </p>
          </div>
          <Toggle checked={isAdmin} onChange={setIsAdmin} />
        </div>

        {/* External domain toggle */}
        <div role="group" aria-label="Allow external domain" className={`flex items-center justify-between py-3 px-4 rounded-lg border mb-4 transition-colors ${
          looksExternal
            ? "bg-fill-hex-fffbeb dark:bg-fill-hex-d97706/10 border-edge-amber-200 dark:border-edge-hex-d97706/40"
            : "bg-surface-muted-detail dark:bg-surface-muted-detail border-edge-gray-200 dark:border-edge-dialog-border"
        }`}>
          <div>
            <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Allow external domain</p>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
              {looksExternal
                ? `${emailDomain} is outside ${normalizedOrgDomain}.${allowExternal ? "" : " Enable this to proceed."}`
                : "Invite someone outside your organisation’s domain"}
            </p>
          </div>
          <Toggle checked={allowExternal} onChange={setAllowExternal} />
        </div>

        {settingsError && <p role="status" className="text-xs text-ink-red-600 dark:text-ink-red-300 mb-3">{settingsError}</p>}

        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-xs text-ink-red-600 dark:text-ink-red-300 mb-3"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <div className="flex justify-end gap-3">
          <SecondaryButton onClick={onCancel} disabled={loading}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={loading || inviteBlocked}>
            {loading ? "Sending…" : "Send Invite"}
          </PrimaryButton>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Remove Confirm Modal ─────────────────────────────────────────────────────
const RemoveModal = ({ user, userStatus, onClose, onConfirm, loading }) => {
  const isCancelInvite = userStatus === "invited" || userStatus === "expired";
  return (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay/40 backdrop-blur-sm"
    onClick={(e) => e.target === e.currentTarget && onClose()}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 8 }}
      transition={{ duration: 0.18 }}
    >
      <ModalWrapper title={isCancelInvite ? "Cancel Invitation" : "Disable User"} onClose={onClose}>
        <div className="p-6 flex flex-col gap-3 overflow-y-auto">
          <p className="text-sm text-ink-ink dark:text-ink-gray-300">
            {isCancelInvite ? (
              <>Are you sure you want to cancel the invitation for{" "}
                <span className="font-semibold">{user.name || user.email}</span>?
                Their account and all associated data will be permanently removed.
              </>
            ) : (
              <>Are you sure you want to disable{" "}
                <span className="font-semibold">{user.name || user.email}</span>
                ? Their account will be disabled.
              </>
            )}
          </p>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
            {isCancelInvite
              ? "This cannot be undone. To give them access again, you will need to send a new invitation."
              : "This action can be reversed by restoring the user."}
          </p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-edge-border dark:border-edge-gray-700 flex-shrink-0">
          <SecondaryButton onClick={onClose} disabled={loading}>
            Cancel
          </SecondaryButton>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="rounded-lg py-[10px] px-[16px] text-sm font-semibold text-ink-on-color bg-fill-red-600 hover:bg-fill-red-700 disabled:opacity-50 transition-colors"
          >
            {loading
              ? (isCancelInvite ? "Cancelling…" : "Disabling…")
              : (isCancelInvite ? "Cancel Invitation" : "Disable User")}
          </button>
        </div>
      </ModalWrapper>
    </motion.div>
  </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatLastActive = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ─── User Row ─────────────────────────────────────────────────────────────────
// Non-SSO providers — name is managed locally in Photon
const NON_SSO_PROVIDERS = new Set(["password", "emailLink", "email"]);

// Map provider ID to a human-readable label
const providerLabel = (provider) => {
  if (!provider) return null;
  if (provider === "google.com") return "Google";
  if (provider === "microsoft.com") return "Microsoft";
  if (provider === "apple.com") return "Apple";
  if (provider === "github.com") return "GitHub";
  return provider; // fallback to raw value
};

// SVG icons per provider — returns null for unknown (falls back to generic shield)
const ProviderIcon = ({ provider }) => {
  if (provider === "google.com") return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
  if (provider === "microsoft.com") return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.4 2H2v9.4h9.4V2z" fill="#F25022"/>
      <path d="M22 2h-9.4v9.4H22V2z" fill="#7FBA00"/>
      <path d="M11.4 12.6H2V22h9.4v-9.4z" fill="#00A4EF"/>
      <path d="M22 12.6h-9.4V22H22v-9.4z" fill="#FFB900"/>
    </svg>
  );
  if (provider === "apple.com") return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
      <path d="M16.05 2c.08 1.02-.28 2.03-.93 2.78-.65.75-1.67 1.33-2.7 1.25-.1-.98.3-2 .93-2.72.65-.76 1.7-1.33 2.7-1.31zM19.98 17.1c-.48 1.05-1.04 2-1.73 2.82-.96 1.13-1.97 2.25-3.47 2.28-1.46.03-1.94-.87-3.61-.86-1.68.01-2.19.89-3.67.86-1.47-.04-2.43-1.1-3.4-2.24C2.22 17.6 1.04 14.3 1.04 11.1c0-4.46 2.9-6.82 5.76-6.86 1.44-.03 2.8.97 3.68.97.88 0 2.52-1.2 4.26-1.02.72.03 2.77.29 4.07 2.2-.1.07-2.43 1.42-2.4 4.23.03 3.36 2.94 4.47 2.97 4.48-.03.08-.46 1.59-1.4 3z"/>
    </svg>
  );
  // Generic SSO shield for unknown providers
  return (
    <svg className="w-3.5 h-3.5 text-ink-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
};

const UserRow = ({ user, currentUserId, onRemove, onRestore, onPurge, onToggleAdmin, onUpdateName, onResetPassword, pendingReset, onSetPendingReset, onResendInvite, allowPurge, userGroups, onViewDetail }) => {
  const isSelf = user.user_id === currentUserId;
  const isSSO = !!user.auth_provider && !NON_SSO_PROVIDERS.has(user.auth_provider);
  const isPasswordUser = user.auth_provider === "password";
  const [toggling, setToggling] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user.name || "");
  const [savingName, setSavingName] = useState(false);

  const joinedDate = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

  const lastActive = formatLastActive(user.last_active);

  const handleAdminToggle = async () => {
    if (isSelf || toggling) return;
    setToggling(true);
    try {
      await onToggleAdmin(user.user_id, !user.org_mode);
    } finally {
      setToggling(false);
    }
  };

  const handleNameSave = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === (user.name || "")) {
      setEditingName(false);
      setNameValue(user.name || "");
      return;
    }
    setSavingName(true);
    try {
      await onUpdateName(user.user_id, trimmed);
      setEditingName(false);
    } catch {
      setNameValue(user.name || "");
    } finally {
      setSavingName(false);
    }
  };

  const handleNameCancel = () => {
    setEditingName(false);
    setNameValue(user.name || "");
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.15 }}
      className="flex items-center gap-4 px-5 py-4 border-b border-edge-hex-eeeeee dark:border-edge-dialog-border last:border-0 hover:bg-fill-gray-50 dark:hover:bg-fill-card-surface-dark transition-colors"
    >
      <UserAvatar name={user.name} email={user.email} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {editingName ? (
            <input
              autoFocus
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNameSave();
                if (e.key === "Escape") handleNameCancel();
              }}
              onBlur={handleNameSave}
              disabled={savingName}
              className="text-sm font-semibold bg-transparent border-b border-edge-gradient-primary dark:border-edge-accent outline-none text-ink-ink dark:text-ink-white w-40"
            />
          ) : (
            <span
              className={`text-sm font-semibold text-ink-ink dark:text-ink-white truncate ${
                isSSO ? "cursor-default" : "cursor-pointer hover:underline decoration-dotted underline-offset-2"
              }`}
              onClick={() => { if (!isSSO) { setNameValue(user.name || ""); setEditingName(true); } }}
              title={isSSO ? `Name is managed by ${providerLabel(user.auth_provider)}` : "Click to edit name"}
            >
              {user.name || "—"}
            </span>
          )}
          {isSelf && (
              <span className="text-[10px] font-medium text-ink-gradient-primary dark:text-ink-accent bg-fill-blue-50 dark:bg-fill-gradient-primary/15 px-2 py-0.5 rounded-full">
                You
              </span>
            )}
            {user.org_mode && <AdminBadge />}
            {user.is_external && <ExternalBadge />}
            {isSSO && (
              <span title={`Name managed by ${providerLabel(user.auth_provider)}`} className="inline-flex items-center">
                <ProviderIcon provider={user.auth_provider} />
              </span>
            )}
        </div>
        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 truncate mt-0.5">
          {user.email}
        </p>
      </div>

      <div className="w-24 flex-shrink-0"><StatusBadge status={getUserStatus(user)} /></div>

      <div className="flex items-center gap-2 w-20 justify-end flex-shrink-0" title={isSelf ? "Cannot change your own admin role" : ""}>
        <Toggle
          checked={!!user.org_mode}
          onChange={handleAdminToggle}
          disabled={isSelf || toggling}
        />
      </div>

      <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500 w-28 text-right flex-shrink-0 hidden sm:block" title={user.last_active ? new Date(user.last_active).toLocaleString() : undefined}>
        {lastActive}
      </span>

      <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500 w-28 text-right flex-shrink-0 hidden sm:block">
        {joinedDate}
      </span>

      {/* Groups chips */}
      <div className="w-32 flex-shrink-0 hidden lg:flex items-center gap-1 flex-wrap">
        {userGroups === undefined || userGroups === null ? (
          <span className="text-xs text-ink-gray-400">—</span>
        ) : userGroups.length === 0 ? (
          <span className="text-xs text-ink-gray-400">—</span>
        ) : (
          <>
            {userGroups.slice(0, 2).map((g) => (
              <span
                key={g.group_id}
                className="inline-block text-[10px] bg-fill-blue-50 dark:bg-fill-gradient-primary/15 text-ink-gradient-primary dark:text-ink-blue-300 px-1.5 py-0.5 rounded-full font-medium max-w-[56px] truncate"
                title={g.display_name || g.group_id}
              >
                {g.display_name || g.group_id}
              </span>
            ))}
            {userGroups.length > 2 && (
              <span className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400">+{userGroups.length - 2}</span>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-1 w-24 justify-end flex-shrink-0">
        {/* View detail button */}
        {onViewDetail && (
          <button
            onClick={() => onViewDetail(user)}
            title="View groups &amp; access"
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-fill-blue-50 dark:hover:bg-fill-gradient-primary/20 text-ink-gray-400 hover:text-ink-gradient-primary dark:hover:text-ink-accent"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </button>
        )}
        {(() => {
          const userStatus = getUserStatus(user);

          if (userStatus === "disabled") {
            return (
              <>
                {allowPurge && (
                  <button
                    onClick={() => onPurge(user)}
                    title="Permanently delete user"
                    className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6L18.1 20.1A2 2 0 0 1 16.1 22H7.9a2 2 0 0 1-2-1.9L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => onRestore(user.user_id)}
                  title="Restore user"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-fill-green-100 dark:hover:bg-fill-green-600/20 text-ink-gray-400 hover:text-ink-green-600 dark:hover:text-ink-green-300"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                </button>
              </>
            );
          }

          if (userStatus === "invited" || userStatus === "expired") {
            return (
              <>
                {/* Resend invite */}
                <button
                  onClick={() => onResendInvite && onResendInvite(user.user_id)}
                  title="Resend invite"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-fill-blue-50 dark:hover:bg-fill-gradient-primary/20 text-ink-gray-400 hover:text-ink-gradient-primary dark:hover:text-ink-accent"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 12 13 21 6" />
                    <rect x="3" y="6" width="18" height="13" rx="2" />
                    <line x1="12" y1="13" x2="12" y2="19" />
                    <polyline points="9 16 12 13 15 16" />
                  </svg>
                </button>
                {/* Cancel invitation (disable) */}
                <button
                  onClick={() => onRemove(user)}
                  title="Cancel invitation"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                </button>
              </>
            );
          }

          // active
          return (
            <>
              {isPasswordUser && !isSelf ? (
                pendingReset === user.user_id ? (
                  <>
                    <button
                      onClick={() => { onResetPassword(user.user_id, user.email); onSetPendingReset(null); }}
                      title="Confirm — send reset email"
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-colors bg-fill-green-100 dark:bg-fill-green-600/20 text-ink-green-800 dark:text-ink-green-300 hover:opacity-80"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => onSetPendingReset(null)}
                      title="Cancel"
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => onSetPendingReset(user.user_id)}
                    title="Send password reset email"
                    className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-fill-blue-50 dark:hover:bg-fill-gradient-primary/20 text-ink-gray-400 hover:text-ink-gradient-primary dark:hover:text-ink-accent"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </button>
                )
              ) : (
                <span className="w-8 h-8" />
              )}
              <button
                onClick={() => !isSelf && onRemove(user)}
                disabled={isSelf}
                title={isSelf ? "Cannot disable your own account" : "Disable user"}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isSelf
                    ? "opacity-30 cursor-not-allowed"
                    : "hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300"
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
              </button>
            </>
          );
        })()}
      </div>
    </motion.div>
  );
};

// ─── Purge Confirm Modal ──────────────────────────────────────────────────────
const PurgeModal = ({ user, onClose, onConfirm, loading }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay/40 backdrop-blur-sm"
    onClick={(e) => e.target === e.currentTarget && onClose()}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 8 }}
      transition={{ duration: 0.18 }}
    >
      <ModalWrapper title="Permanently Delete User" onClose={onClose}>
        <div className="p-6 flex flex-col gap-3 overflow-y-auto">
          <p className="text-sm text-ink-ink dark:text-ink-gray-300">
            You are about to permanently delete{" "}
            <span className="font-semibold">{user.name || user.email}</span>.
            Their account will be removed immediately and cannot be recovered.
          </p>
          <ul className="text-xs text-ink-gray-500 dark:text-ink-gray-400 flex flex-col gap-1 list-disc list-inside">
            <li>All chats, files, and scheduled prompts will be deleted.</li>
            <li>Spectras shared with others will be transferred to you.</li>
            <li>Solo spectras with no collaborators will be deleted.</li>
          </ul>
          <p className="text-xs font-medium text-ink-red-600 dark:text-ink-red-300">
            This action is irreversible.
          </p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-edge-border dark:border-edge-gray-700 flex-shrink-0">
          <SecondaryButton onClick={onClose} disabled={loading}>
            Cancel
          </SecondaryButton>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="rounded-lg py-[10px] px-[16px] text-sm font-semibold text-ink-on-color bg-fill-red-600 hover:bg-fill-red-700 disabled:opacity-50 transition-colors"
          >
            {loading ? "Deleting…" : "Delete Permanently"}
          </button>
        </div>
      </ModalWrapper>
    </motion.div>
  </div>
);

// ─── Signup Mode Card ────────────────────────────────────────────────────────
const SIGNUP_MODES = [
  {
    value: "domain_open",
    label: "Domain open",
    description: "Domain users sign in automatically. External users must be invited.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    ),
    accent: { text: "text-ink-gradient-primary", border: "border-edge-blue-200 dark:border-edge-gradient-primary/30", bg: "bg-fill-blue-50 dark:bg-fill-gradient-primary/10", radio: "border-edge-gradient-primary", dot: "bg-fill-gradient-primary", icon: "text-ink-gradient-primary" },
  },
  {
    value: "invite_only",
    label: "Invite only",
    description: "All users must be invited by an admin before they can sign in.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    accent: { text: "text-ink-violet-600", border: "border-edge-hex-ddd6fe dark:border-edge-violet-600/30", bg: "bg-fill-hex-ede9fe dark:bg-fill-violet-600/10", radio: "border-edge-violet-600", dot: "bg-fill-violet-600", icon: "text-ink-violet-600" },
  },
  {
    value: "public",
    label: "Public",
    description: "Anyone can sign in. External users are automatically tagged.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
      </svg>
    ),
    accent: { text: "text-ink-hex-d97706", border: "border-edge-amber-200 dark:border-edge-hex-d97706/40", bg: "bg-fill-hex-fffbeb dark:bg-fill-hex-d97706/10", radio: "border-edge-hex-d97706", dot: "bg-fill-hex-d97706", icon: "text-ink-hex-d97706" },
  },
];

const AccessControlCard = ({ signupMode, onChange, saving, allowPurge, onAllowPurgeChange, savingPurge, clientIntegrationEnabled, onClientIntegrationChange, savingClientIntegration, orgDomain, orgId, isPrimary }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl mb-4 overflow-hidden">
      {/* Collapsed header — always visible */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-5 py-4 text-left hover:bg-fill-gray-50 dark:hover:bg-fill-card-hover-dark transition-colors"
      >
        <svg
          className={`w-4 h-4 text-ink-gray-400 transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
        <p className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">Access Control</p>
      </button>

      {/* Expandable options */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="access-control-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 pt-3 border-t border-edge-gray-200 dark:border-edge-dialog-border">
              {(orgId || orgDomain) && (
                <div className="mb-4 pb-4 border-b border-edge-gray-200 dark:border-edge-dialog-border space-y-3">
                  {orgId && (
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Org</p>
                        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">The unique identifier for this organisation.</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isPrimary && (
                          <span className="text-xs font-medium text-ink-emerald-700 dark:text-ink-emerald-400 bg-fill-emerald-50 dark:bg-fill-emerald-900/30 border border-edge-hex-a7f3d0 dark:border-edge-hex-065f46 px-2 py-0.5 rounded-full">Primary</span>
                        )}
                        <span className="text-sm font-mono text-ink-ink dark:text-ink-white bg-fill-gray-100 dark:bg-fill-dialog-border px-3 py-1 rounded-lg">{orgId}</span>
                      </div>
                    </div>
                  )}
                  {orgDomain && (
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Domain</p>
                        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">The email domain associated with this organisation.</p>
                      </div>
                      <span className="text-sm font-mono text-ink-ink dark:text-ink-white bg-fill-gray-100 dark:bg-fill-dialog-border px-3 py-1 rounded-lg">{orgDomain}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {SIGNUP_MODES.map((mode) => {
                  const isSelected = signupMode === mode.value;
                  const a = mode.accent;
                  return (
                    <button
                      key={mode.value}
                      onClick={() => !saving && onChange(mode.value)}
                      disabled={saving}
                      className={`relative flex flex-col items-center text-center gap-3 px-3 py-4 rounded-xl border transition-colors ${
                        isSelected
                          ? `${a.bg} ${a.border}`
                          : "bg-fill-gray-50 dark:bg-fill-card-surface-dark border-edge-gray-200 dark:border-edge-dialog-border hover:border-edge-slate-300 dark:hover:border-edge-zinc-600"
                      } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      {/* Radio — top right */}
                      <span className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? a.radio : "border-edge-gray-300 dark:border-edge-zinc-600"
                      }`}>
                        {isSelected && <span className={`w-2 h-2 rounded-full ${a.dot}`} />}
                      </span>

                      {/* Icon */}
                      <span className={`${isSelected ? a.icon : "text-ink-gray-400 dark:text-ink-gray-500"} transition-colors`}>
                        {mode.icon}
                      </span>

                      {/* Label + description */}
                      <div>
                        <p className={`text-sm font-semibold ${isSelected ? a.text : "text-ink-ink dark:text-ink-white"}`}>
                          {mode.label}
                        </p>
                        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-1 leading-relaxed">
                          {mode.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {saving && <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500 mt-3">Saving…</p>}

              {/* Allow purge toggle */}
              <div className="mt-4 pt-4 border-t border-edge-gray-200 dark:border-edge-dialog-border flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Allow permanent deletion</p>
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                    Permits admins to permanently purge disabled users and all their data.
                  </p>
                </div>
                <Toggle checked={allowPurge} onChange={onAllowPurgeChange} disabled={savingPurge} />
              </div>

              {/* Client Integration toggle */}
              <div className="mt-4 pt-4 border-t border-edge-gray-200 dark:border-edge-dialog-border flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Client Integration</p>
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                    Enable the Client Integration API for this org. When off, all <code className="font-mono">/api/v1/clients/</code> endpoints return 403.
                  </p>
                </div>
                <Toggle checked={clientIntegrationEnabled} onChange={onClientIntegrationChange} disabled={savingClientIntegration} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const UsersPage = () => {
  const { showFeedback } = useFeedback();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [signupMode, setSignupMode] = useState("domain_open");
  const [savingMode, setSavingMode] = useState(false);
  const [allowPurge, setAllowPurge] = useState(true);
  const [savingPurge, setSavingPurge] = useState(false);
  const [clientIntegrationEnabled, setClientIntegrationEnabled] = useState(false);
  const [savingClientIntegration, setSavingClientIntegration] = useState(false);
  const [orgDomain, setOrgDomain] = useState("");
  const [orgSettingsStatus, setOrgSettingsStatus] = useState("loading");
  const [orgId, setOrgId] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [pendingReset, setPendingReset] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userGroupsMap, setUserGroupsMap] = useState({});

  const currentUserId = localStorage.getItem("user_id");

  const fetchUsers = useCallback(async ({ showLoader = true } = {}) => {
    if (showLoader) setLoading(true);
    try {
      const data = await listAdminUsers();
      // Exclude users mid-purge — the background task will hard-delete them shortly
      const visibleUsers = data.filter((u) => !u.purge_in_progress);
      setUsers(visibleUsers);
      // Load group memberships for all users in the background
      const orgId = UserAuthStore.getState().orgId;
      if (orgId && visibleUsers.length) {
        const results = await Promise.allSettled(
          visibleUsers.map((u) => getUserGroups(u.user_id, orgId).then((groups) => ({ userId: u.user_id, groups })))
        );
        const map = {};
        results.forEach((r) => {
          if (r.status === "fulfilled") map[r.value.userId] = r.value.groups ?? [];
        });
        setUserGroupsMap(map);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load users.", "error");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    fetchUsers();
    getOrgSettings().then((s) => {
      setSignupMode(s.signup_mode || "domain_open");
      setAllowPurge(s.allow_purge !== false);
      setClientIntegrationEnabled(s.client_integration_enabled === true);
      setOrgDomain(s.domain || "");
      setOrgId(s.org_id || "");
      setIsPrimary(s.is_primary === true);
      setOrgSettingsStatus("ready");
    }).catch((err) => {
      // Previously silently swallowed — these settings drive several visible
      // admin toggles (allowPurge, clientIntegrationEnabled, etc.), so a failed
      // load left the page showing default values with zero indication that
      // the real org settings never actually loaded.
      logger.error("UsersPage", "Failed to load org settings", { error: err?.message });
      setOrgSettingsStatus("error");
      showFeedback("Failed to load organization settings.", "error");
    });

    // Refetch when the admin switches back to this tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchUsers({ showLoader: false });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Refetch every 60 seconds to catch external changes (e.g. Firebase console deletions)
    const pollInterval = setInterval(() => fetchUsers({ showLoader: false }), 60000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(pollInterval);
    };
  }, [fetchUsers, showFeedback]);

  const handleSignupModeChange = async (newMode) => {
    const prev = signupMode;
    setSignupMode(newMode);
    setSavingMode(true);
    try {
      await updateOrgSettings({ signupMode: newMode });
    } catch {
      setSignupMode(prev);
      showFeedback("Failed to update access control.", "error");
    } finally {
      setSavingMode(false);
    }
  };

  const handleAllowPurgeChange = async (newValue) => {
    const prev = allowPurge;
    setAllowPurge(newValue);
    setSavingPurge(true);
    try {
      await updateOrgSettings({ allowPurge: newValue });
    } catch {
      setAllowPurge(prev);
      showFeedback("Failed to update purge setting.", "error");
    } finally {
      setSavingPurge(false);
    }
  };

  const handleClientIntegrationChange = async (newValue) => {
    const prev = clientIntegrationEnabled;
    setClientIntegrationEnabled(newValue);
    setSavingClientIntegration(true);
    try {
      await updateOrgSettings({ clientIntegrationEnabled: newValue });
    } catch {
      setClientIntegrationEnabled(prev);
      showFeedback("Failed to update Client Integration setting.", "error");
    } finally {
      setSavingClientIntegration(false);
    }
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    setRemoveLoading(true);
    const status = getUserStatus(removeTarget);
    const isCancelInvite = status === "invited" || status === "expired";
    try {
      if (isCancelInvite) {
        await cancelAdminUserInvite(removeTarget.user_id);
        showFeedback(`Invitation for ${removeTarget.name || removeTarget.email} has been cancelled.`, "success");
      } else {
        await disableAdminUser(removeTarget.user_id);
        showFeedback(`${removeTarget.name || removeTarget.email} has been disabled.`, "success");
      }
      setRemoveTarget(null);
      await fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || (isCancelInvite ? "Failed to cancel invitation." : "Failed to remove user.");
      showFeedback(msg, "error");
    } finally {
      setRemoveLoading(false);
    }
  };

  const handlePurgeConfirm = async () => {
    if (!purgeTarget) return;
    setPurgeLoading(true);
    const target = purgeTarget;
    try {
      await purgeAdminUser(target.user_id);
      // Optimistically remove from list immediately — background cleanup is housekeeping
      setUsers((prev) => prev.filter((u) => u.user_id !== target.user_id));
      setPurgeTarget(null);
      showFeedback(`${target.name || target.email} has been permanently deleted.`, "success");
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Failed to purge user.";
      showFeedback(msg, "error");
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleInviteSuccess = () => {
    setShowInviteForm(false);
    fetchUsers();  };

  const handleRestoreUser = async (userId) => {
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) => u.user_id === userId ? { ...u, disabled: false, removed_at: null } : u)
    );
    try {
      await restoreAdminUser(userId);
      showFeedback("User has been restored.", "success");
    } catch (err) {
      // Revert on failure
      setUsers((prev) =>
        prev.map((u) => u.user_id === userId ? { ...u, disabled: true } : u)
      );
      const msg = err?.response?.status === 409
        ? err.response.data?.error
        : (err?.response?.data?.error || "Failed to restore user.");
      showFeedback(msg, "error");
    }
  };

  const handleResetPassword = async (userId, email) => {
    try {
      await resetAdminUserPassword(userId);
      showFeedback(`Password reset email sent to ${email}.`, "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to send password reset email.", "error");
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      const result = await resendAdminUserInvite(userId);
      // Refresh user list so invite_token_expires_at reflects the new 24h window
      const fresh = await listAdminUsers();
      setUsers(fresh);
      const feedback = getUserInviteFeedback(result, true);
      showFeedback(feedback.message, feedback.type);
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to resend invite.", "error");
    }
  };

  const handleToggleAdmin = async (userId, newOrgMode) => {
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) => u.user_id === userId ? { ...u, org_mode: newOrgMode } : u)
    );
    try {
      await updateAdminUserOrgMode(userId, newOrgMode);
      showFeedback(`Admin access ${newOrgMode ? "granted" : "revoked"}.`, "success");
    } catch (err) {
      // Revert on failure
      setUsers((prev) =>
        prev.map((u) => u.user_id === userId ? { ...u, org_mode: !newOrgMode } : u)
      );
      showFeedback(err?.response?.data?.error || "Failed to update admin access.", "error");
    }
  };

  const handleUpdateUserName = async (userId, newName) => {
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) => u.user_id === userId ? { ...u, name: newName } : u)
    );
    try {
      await updateAdminUserName(userId, newName);
      showFeedback("Name updated.", "success");
    } catch (err) {
      // Revert on failure — refetch to get server state
      await fetchUsers();
      showFeedback(err?.response?.data?.error || "Failed to update name.", "error");
      throw err;
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = searchQuery.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const activeCount = users.filter((u) => !u.disabled).length;

  return (
    <SettingsPageShell
      title="Users"
      description="Manage your organisation's members, assign roles, resend invitations, and control who has access to Photon AI features and resources."
      action={!loading && users.length > 0 ? { label: "Invite User", onClick: () => setShowInviteForm((v) => !v) } : undefined}
      maxWidth="5xl"
    >
      <div className="mt-0">

          {/* Toolbar — only shown once loaded and users exist */}
          {!loading && users.length > 0 && (
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-3 flex-1 max-w-sm">
              <div className="relative flex-1">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-gray-400 dark:text-ink-gray-500 w-4 h-4 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search users…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="photon-field w-full pl-9 pr-3 py-2 text-sm bg-fill-hex-fafbfe dark:bg-fill-navigation-surface-dark border border-edge-hex-aaaaaa dark:border-edge-hex-aaaaaa80 rounded-[10px] text-ink-ink dark:text-ink-white placeholder-ink-copy-hover placeholder:italic outline-none"
                />
              </div>
              <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500 whitespace-nowrap">
                {activeCount} active · {users.length} total
              </span>
            </div>
          </div>
          )}

          {/* Access control */}
          <AccessControlCard signupMode={signupMode} onChange={handleSignupModeChange} saving={savingMode} allowPurge={allowPurge} onAllowPurgeChange={handleAllowPurgeChange} savingPurge={savingPurge} clientIntegrationEnabled={clientIntegrationEnabled} onClientIntegrationChange={handleClientIntegrationChange} savingClientIntegration={savingClientIntegration} orgDomain={orgDomain} orgId={orgId} isPrimary={isPrimary} />

          {/* Inline invite form */}
          <AnimatePresence initial={false}>
            {showInviteForm && (
              <InviteForm
                orgDomain={orgDomain}
                orgSettingsStatus={orgSettingsStatus}
                onSuccess={handleInviteSuccess}
                onCancel={() => setShowInviteForm(false)}
              />
            )}
          </AnimatePresence>

          {/* Table */}
          <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="hidden sm:flex items-center gap-4 px-5 py-2.5 bg-surface-muted-detail dark:bg-surface-muted-detail border-b border-edge-hex-eeeeee dark:border-edge-dialog-border">
              <div className="w-9 flex-shrink-0" />
              <span className="flex-1 text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">
                User
              </span>
              <span className="w-24 flex-shrink-0 text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">
                Status
              </span>
              <span className="w-20 text-right text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide flex-shrink-0">
                Admin
              </span>
              <span className="w-28 text-right text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide flex-shrink-0">
                Last Active
              </span>
              <span className="w-28 text-right text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide flex-shrink-0">
                Joined
              </span>
              <span className="w-32 text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide flex-shrink-0 hidden lg:block">
                Groups
              </span>
              <div className="w-24 flex-shrink-0" />
            </div>

            {/* User list */}
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader />
              </div>
            ) : filteredUsers.length === 0 && !showInviteForm ? (
              <EmptyState
                icon={<UserPlus className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
                title={searchQuery ? "No users match your search" : "No users yet"}
                description={searchQuery ? "Try a different name or email." : "Invite your first team member to get started."}
                action={searchQuery ? undefined : { label: "Invite User", onClick: () => setShowInviteForm(true) }}
                className="py-16"
              />
            ) : (
              <AnimatePresence initial={false}>
                {filteredUsers.map((user) => (
                  <UserRow
                    key={user.user_id}
                    user={user}
                    currentUserId={currentUserId}
                    onRemove={setRemoveTarget}
                    onRestore={handleRestoreUser}
                    onPurge={setPurgeTarget}
                    onToggleAdmin={handleToggleAdmin}
                    onUpdateName={handleUpdateUserName}
                    onResetPassword={handleResetPassword}
                    pendingReset={pendingReset}
                    onSetPendingReset={setPendingReset}
                    onResendInvite={handleResendInvite}
                    allowPurge={allowPurge}
                    userGroups={userGroupsMap[user.user_id]}
                    onViewDetail={setSelectedUser}
                  />
                ))}
              </AnimatePresence>
            )}
          </div>
      </div>

      {/* Remove confirm modal */}
      <AnimatePresence>
        {removeTarget && (
          <RemoveModal
            user={removeTarget}
            userStatus={getUserStatus(removeTarget)}
            onClose={() => !removeLoading && setRemoveTarget(null)}
            onConfirm={handleRemoveConfirm}
            loading={removeLoading}
          />
        )}
      </AnimatePresence>

      {/* Purge confirm modal */}
      <AnimatePresence>
        {purgeTarget && (
          <PurgeModal
            user={purgeTarget}
            onClose={() => !purgeLoading && setPurgeTarget(null)}
            onConfirm={handlePurgeConfirm}
            loading={purgeLoading}
          />
        )}
      </AnimatePresence>

      {/* User detail panel — Groups + Resolved Access */}
      <AnimatePresence>
        {selectedUser && (
          <UserDetailPanel
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
            onGroupsChanged={fetchUsers}
          />
        )}
      </AnimatePresence>
    </SettingsPageShell>
  );
};

export default UsersPage;
